import type { CoreError } from '@ingenico/api-sdk-core/common';
import { ALL_LNGS_NAMESPACES } from '@ingenico/util-i18n-config';
import { Level, showNotification } from '@ingenico/react-notifications';
import { useBusinessService } from '@ingenico/react-section-utils';
import { downloadFile, requestAndUpdateState } from '@ingenico/react-utils/functions';
import type { UseAsyncDataState } from '@ingenico/react-utils/hooks';
import { useAsyncDataState } from '@ingenico/react-utils/hooks';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DeviceManagementService } from '../domain/business/services/deviceManagement.service';

export interface UseDownloadDeviceLogFileParam {
  readonly logicalDeviceId: string;
  readonly fileId: string;
  readonly filename: string;
}

export interface UseDownloadDeviceLogFileReturn {
  readonly download: (params: UseDownloadDeviceLogFileParam) => void;
  readonly downloadDeviceLogFileState: UseAsyncDataState<Blob, CoreError>;
}

export function useDownloadDeviceLogFile(): UseDownloadDeviceLogFileReturn {
  const { t } = useTranslation([ALL_LNGS_NAMESPACES.COMMON]);

  const deviceManagementService = useBusinessService(DeviceManagementService);

  const [downloadDeviceLogFileState, setDownloadDeviceLogFile] = useAsyncDataState<Blob, CoreError>();

  const onError = useCallback(() => {
    showNotification({
      content: t('error.default', { ns: ALL_LNGS_NAMESPACES.COMMON }),
      level: Level.Error,
    });
  }, [t]);

  const download = useCallback(
    ({ fileId, logicalDeviceId, filename }: UseDownloadDeviceLogFileParam) => {
      requestAndUpdateState(
        deviceManagementService.downloadDeviceLogFile(logicalDeviceId, fileId),
        setDownloadDeviceLogFile,
        {
          onSuccess(content) {
            const file = new Blob([content]);
            downloadFile(file, filename);
          },
          onError,
        },
      );
    },
    [onError, deviceManagementService, setDownloadDeviceLogFile],
  );

  return {
    download,
    downloadDeviceLogFileState,
  };
}
